import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  clearClientVersionOverride,
  getClientVersion,
  getClientVersionOverride,
  setClientVersionOverride,
} from '../../services/client-version/client-version';
import { notifyVersionMismatch } from '../../services/client-version/version-mismatch-events';
import { Button } from '../components/button/button';
import { PageContainer } from '../components/page-container';
import { Stack } from '../components/stack/stack';
import { TextBox } from '../components/text-box/text-box';
import { useToaster } from '../state/toaster-state';
import { useOptionalUserState } from '../state/user-state/user-state';

/**
 * Org-admin-only manual test page for SK-5052. Writes a `CLIENT_VERSION`
 * override to localStorage so the `skymap-client-version` request header can
 * be forced to a specific value — useful for provoking the backend into
 * responding with `X-Skymap-Client-Stale: 1` and exercising the in-session
 * mismatch toast end-to-end.
 *
 * The override is cleared automatically when the stale signal is observed
 * (see `SkyMapAxiosServiceFactory.checkClientStale`), so the mandatory
 * reload does not re-trigger the same mismatch.
 */
export const DevToolsClientVersionRoute = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin;

  const { showToast } = useToaster();
  const [draft, setDraft] = React.useState<string>(() => getClientVersionOverride() ?? '');
  const [effective, setEffective] = React.useState<string>(() => getClientVersion());
  const [override, setOverride] = React.useState<string | null>(() => getClientVersionOverride());

  const refresh = React.useCallback(() => {
    setOverride(getClientVersionOverride());
    setEffective(getClientVersion());
  }, []);

  const handleApply = React.useCallback(() => {
    setClientVersionOverride(draft);
    refresh();
    showToast({
      type: 'success',
      title: t('clientVersionOverride.applied.title', { ns: 'common' }),
      message: t('clientVersionOverride.applied.message', { ns: 'common' }),
    });
  }, [draft, refresh, showToast, t]);

  const handleClear = React.useCallback(() => {
    clearClientVersionOverride();
    setDraft('');
    refresh();
    showToast({
      type: 'info',
      title: t('clientVersionOverride.cleared.title', { ns: 'common' }),
      message: t('clientVersionOverride.cleared.message', { ns: 'common' }),
    });
  }, [refresh, showToast, t]);

  const handleSimulate = React.useCallback(() => {
    notifyVersionMismatch();
  }, []);

  if (isOrganizationAdmin === undefined) {
    return null;
  }

  if (!isOrganizationAdmin) {
    return <Navigate replace={true} to="/projects" />;
  }

  return (
    <PageContainer title={t('clientVersionOverride.title', { ns: 'common' })}>
      <Stack spacing={1}>
        <Description>{t('clientVersionOverride.description', { ns: 'common' })}</Description>
        <Stack direction="row" spacing={0.5}>
          <Label>{t('clientVersionOverride.effectiveLabel', { ns: 'common' })}</Label>
          <Value>{effective || t('clientVersionOverride.emptyValue', { ns: 'common' })}</Value>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Label>{t('clientVersionOverride.overrideLabel', { ns: 'common' })}</Label>
          <Value>{override ?? t('clientVersionOverride.noOverride', { ns: 'common' })}</Value>
        </Stack>
        <FormRow>
          <TextBox
            placeholder={t('clientVersionOverride.inputPlaceholder', { ns: 'common' })}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Stack direction="row" spacing={0.5}>
            <Button color="primary" variant="contained" onClick={handleApply}>
              {t('clientVersionOverride.apply', { ns: 'common' })}
            </Button>
            <Button color="secondary" variant="text" onClick={handleClear}>
              {t('clientVersionOverride.clear', { ns: 'common' })}
            </Button>
          </Stack>
        </FormRow>
        <Hint>{t('clientVersionOverride.hint', { ns: 'common' })}</Hint>
        <Stack direction="row" spacing={0.5}>
          <Button color="secondary" variant="contained" onClick={handleSimulate}>
            {t('clientVersionOverride.simulate', { ns: 'common' })}
          </Button>
          <SimulateHint>{t('clientVersionOverride.simulateHint', { ns: 'common' })}</SimulateHint>
        </Stack>
      </Stack>
    </PageContainer>
  );
};

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.gray.dark};
  line-height: 1.45;
`;

const Label = styled.span`
  font-weight: 600;
  min-width: 180px;
`;

const Value = styled.code`
  background: ${({ theme }) => theme.color.gray.lightest};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const FormRow = styled(Stack)`
  gap: 12px;
`;

const Hint = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.gray.dark};
  font-size: 0.85rem;
  font-style: italic;
`;

const SimulateHint = styled.span`
  align-self: center;
  color: ${({ theme }) => theme.color.gray.dark};
  font-size: 0.85rem;
  font-style: italic;
`;
